import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import "./WealthSeriesPortfolioTickerInfo.scss";
import { Tab, Tabs } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExampleChart from "../../../TradingView/ExampleChart";
import { capitalize } from "lodash";
import { connect } from "react-redux";
import { FundamentalData } from "react-tradingview-embed";
import { numberWithCommas } from "../../../utilities";
import BeautifulSkeleton from "../../../common/Skeletons/BeautifulSkeleton";
import { truncateText } from "../../../company/Sentiment";
import { eventEmitter } from "../../../TradingView/datafeed";
import { useAuth } from "../../../../contexts/Auth";
import { isInternalWS } from "../../../../appRedux/actions/helpers";
import { companyDataLoad } from "../../../../appRedux/ducks/companyData";
import { dxFeedSocketListen } from "../../../../appRedux/ducks/dxFeedSocket";
import { portfolioSymbolSetter } from "../../../../appRedux/ducks/portfolioTicker";
import ReactMarkdown from "react-markdown";
import moment from "moment";
import { BN_TICKER_META } from "../../../BitcoinNavigator/constants";

const WealthSeriesPortfolioTickerInfo = ({
  fetchCompanyData,
  companyDataLoading,
  tickerDetails,
  themeMode,
  userSettings,
  ticker,
  marketStatus,
  containerClassName = ".ws-portfolio-content-container",
  setTicker,
  selectedTickerInfo,
  lite = false,
  navigator = false,
  currentPrices,
  dxCurrentSubs,
  listen,
}) => {
  const [value, setValue] = useState(0);
  const [tickerPriceLow, setTickerPriceLow] = useState({});
  const [tickerPriceHigh, setTickerPriceHigh] = useState({});
  const [lastPrice, setLastPrice] = useState("");
  // Bitcoin Navigator hides the tab bar entirely when the drawer content
  // already fits its scroll container.
  const [tabsHidden, setTabsHidden] = useState(false);
  const { isBlurred } = useAuth();
  // References to each section
  const overviewRef = useRef(null);
  const adjustmentReasoningRef = useRef(null);
  const chartRef = useRef(null);
  const financialsRef = useRef(null);
  const aboutRef = useRef(null);
  const contentRootRef = useRef(null);

  // Reference to the parent scrollable container
  const containerRef = useRef(null);

  // Bitcoin Navigator sources the About copy from curated ETF metadata when
  // the company-data API has no description for the fund.
  const displayDescription = navigator
    ? tickerDetails?.tickerDetails?.description ||
      BN_TICKER_META[ticker]?.about ||
      ""
    : tickerDetails?.tickerDetails?.description;

  const showAdjustmentReasoning = !!(!lite && selectedTickerInfo?.reason);

  /* -------------------------------------------------
   * Simple array of section refs — must mirror the tab
   * conditions exactly so tab index === ref index.
   * ------------------------------------------------*/
  const getSectionRefs = useCallback(() => {
    const refs = [];

    if (!navigator) refs.push(overviewRef);
    if (showAdjustmentReasoning) refs.push(adjustmentReasoningRef);
    refs.push(chartRef);
    if (!navigator) refs.push(financialsRef);
    if (displayDescription) refs.push(aboutRef);

    return refs;
  }, [navigator, showAdjustmentReasoning, displayDescription]);

  useEffect(() => {
    // Find the container once on mount
    containerRef.current = document.querySelector(containerClassName);
  }, [containerClassName]);

  // Navigator only: drop the tab bar when the content would fit the scroll
  // container without it. Overflow is normalized by the tab bar's own height
  // so the decision is the same whether the bar is currently mounted or not
  // (no show/hide oscillation).
  useEffect(() => {
    if (!navigator) return undefined;
    const scrollEl = containerRef.current;
    if (!scrollEl || typeof ResizeObserver === "undefined") return undefined;

    let frame = null;
    const measure = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const overflow = scrollEl.scrollHeight - scrollEl.clientHeight;
        setTabsHidden((prev) => {
          const tabsEl = scrollEl.querySelector(
            ".ws-portfolio-ticker-info-tabs",
          );
          const tabsHeight = prev ? 0 : tabsEl?.offsetHeight || 48;
          return overflow - tabsHeight <= 8;
        });
      });
    };

    const observer = new ResizeObserver(measure);
    observer.observe(scrollEl);
    if (contentRootRef.current) observer.observe(contentRootRef.current);
    measure();

    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [navigator, ticker, companyDataLoading]);

  // Simplified tab change handler
  const handleChange = (event, newValue) => {
    setValue(newValue);

    const targetRef = getSectionRefs()[newValue];
    if (targetRef?.current && containerRef.current) {
      // Simple offset to account for header and padding
      const headerHeight = document.querySelector(
        ".ws-portfolio-ticker-info__header",
      ).offsetHeight;
      containerRef.current.scrollTo({
        top:
          targetRef.current.offsetTop -
          containerRef.current.offsetTop -
          headerHeight,
        behavior: "smooth",
      });
    }
  };

  const tickerDetailsInfo = tickerDetails?.tickerDetails || {};

  const memoizedFundamentalData = useMemo(
    () =>
      ticker ? (
        <FundamentalData
          className="hide-scroll"
          widgetProps={{
            symbol: ticker,
            colorTheme: themeMode,
            width: "100%",
            isTransparent: themeMode === "light",
          }}
        />
      ) : null,
    [themeMode, ticker],
  );
  const secPrice = tickerDetails?.secPrice;

  const ohlc = useMemo(() => {
    const open = secPrice?.open_price || 0;
    const high = secPrice?.bounds?.high || 0;
    const low = secPrice?.bounds?.low || 0;
    const close =
      marketStatus === "after" || marketStatus === "closed"
        ? secPrice?.close_price || 0
        : secPrice?.prev_close_price || 0;

    let lastPriceUsed = {
      "pre-open": parseFloat(lastPrice || secPrice?.last_price || 0).toFixed(2),
      open: parseFloat(lastPrice || secPrice?.last_price || 0).toFixed(2),
      after: parseFloat(
        secPrice?.close_price
          ? secPrice?.close_price
          : lastPrice || secPrice?.last_price || 0,
      ).toFixed(2),
      closed: parseFloat(secPrice?.close_price || 0).toFixed(2),
    }[marketStatus];

    let compare = {
      "pre-open": parseFloat(
        secPrice?.pre_open || secPrice?.prev_close_price,
      ).toFixed(2),
      open: parseFloat(secPrice?.open_price).toFixed(2),
      after: parseFloat(secPrice?.open_price).toFixed(2),
      closed: parseFloat(secPrice?.open_price).toFixed(2),
    }[marketStatus];

    return {
      open,
      high,
      low,
      close,
      closeType: secPrice?.close_price ? "Close" : "Previous Close",
      performancePercent: (
        ((lastPriceUsed - compare) / compare) *
        100
      )?.toFixed(2),
      lp: lastPriceUsed,
      change: Number(lastPriceUsed - compare).toFixed(2),
      positivePerformance: ((lastPriceUsed - compare) / compare) * 100 > 0,
      secTicker: secPrice?.bounds.ticker,
    };
  }, [secPrice, marketStatus, lastPrice, ticker]);

  useEffect(() => {
    const keys = Object.keys(tickerPriceHigh);
    if (lastPrice >= ohlc.high && ticker === ohlc.secTicker && !!lastPrice) {
      if (!keys.length) {
        setTickerPriceHigh({
          [ohlc.secTicker]: lastPrice,
        });
      } else {
        if (tickerPriceHigh[ticker] < lastPrice) {
          setTickerPriceHigh({
            [ohlc.secTicker]: lastPrice,
          });
        }
      }
    }
  }, [ohlc, ticker, lastPrice]);

  useEffect(() => {
    const keys = Object.keys(tickerPriceLow);
    if (lastPrice <= ohlc.low && ticker === ohlc.secTicker && !!lastPrice) {
      if (!keys.length) {
        setTickerPriceLow({
          [ohlc.secTicker]: lastPrice,
        });
      } else {
        if (tickerPriceLow[ticker] > lastPrice) {
          setTickerPriceLow({
            [ohlc.secTicker]: lastPrice,
          });
        }
      }
    }
  }, [ohlc, ticker, lastPrice]);

  useEffect(() => {
    setTickerPriceHigh({});
    setTickerPriceLow({});
  }, [ticker]);

  useEffect(() => {
    const handlePriceUpdate = (event) => {
      const currentSub = event.detail;
      if (
        !!currentSub &&
        (currentSub?.eventSymbol?.includes(ticker) ||
          currentSub?.symbol?.includes(ticker))
      ) {
        setLastPrice(currentSub.close);
      }
    };
    eventEmitter.addEventListener("priceUpdate", handlePriceUpdate);

    return () => {
      eventEmitter.removeEventListener("priceUpdate", handlePriceUpdate);
    };
  }, [ticker, setLastPrice, themeMode]);

  // The dxFeed socket duck holds a single global subscription, so joining it
  // must not clobber whoever subscribed before us (e.g. the holdings table).
  // Track the live subscription list in a ref and only (re)subscribe when the
  // drawer's ticker isn't already streaming; restore the prior set on close.
  const dxCurrentSubsRef = useRef(dxCurrentSubs);
  useEffect(() => {
    dxCurrentSubsRef.current = dxCurrentSubs;
  }, [dxCurrentSubs]);

  useEffect(() => {
    if (!ticker) return undefined;
    const priorSubs = dxCurrentSubsRef.current || [];
    if (priorSubs.includes(ticker)) return undefined;

    listen([...new Set([...priorSubs, ticker])], false, false);
    return () => {
      if (priorSubs.length) {
        listen(priorSubs, false, false);
      } else {
        listen([], false, true);
      }
    };
  }, [ticker, listen]);

  // Live price from the shared socket drives the header price and the
  // change / percent figures; the TradingView datafeed listener above stays
  // as a fallback for symbols the socket doesn't cover.
  useEffect(() => {
    const socketPrice = currentPrices?.get?.(ticker);
    if (socketPrice != null && !isNaN(Number(socketPrice))) {
      setLastPrice(Number(socketPrice));
    }
  }, [currentPrices, ticker]);

  const highToShow = useMemo(() => {
    const keys = Object.keys(tickerPriceHigh);
    if (
      !!keys.length &&
      keys[0] === ohlc.secTicker &&
      ohlc.high < tickerPriceHigh[ohlc.secTicker] &&
      ticker === keys[0]
    ) {
      return tickerPriceHigh[ohlc.secTicker];
    }
    return ohlc.high;
  }, [ticker, tickerPriceHigh, ohlc]);

  const lowToShow = useMemo(() => {
    const keys = Object.keys(tickerPriceLow);
    if (
      !!keys.length &&
      keys[0] === ohlc.secTicker &&
      ohlc.low > tickerPriceLow[ohlc.secTicker] &&
      ticker === keys[0]
    ) {
      return tickerPriceLow[ohlc.secTicker];
    }
    return ohlc.low;
  }, [ticker, tickerPriceLow, ohlc]);

  useEffect(() => {
    if (ticker === ohlc.secTicker) {
      setLastPrice(secPrice?.last_price);
    } else {
      setLastPrice();
    }
  }, [ticker, secPrice]);

  useEffect(() => {
    if (ticker) {
      fetchCompanyData(ticker);
    }
  }, [ticker]);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const headerHeight = document.querySelector(
        ".ws-portfolio-ticker-info__header",
      ).offsetHeight;
      const scrollTop = containerRef.current.scrollTop + headerHeight;

      let currentSectionIndex = 0;
      let smallestDistance = Infinity;
      const sectionRefs = getSectionRefs();

      sectionRefs.forEach((ref, index) => {
        if (!ref.current) return;
        const offset = ref.current.offsetTop - containerRef.current.offsetTop;
        const distance = Math.abs(offset - scrollTop);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          currentSectionIndex = index;
        }
      });

      // Update tab value if necessary
      if (currentSectionIndex !== value) {
        setValue(currentSectionIndex);
      }
    };

    if (containerRef.current) {
      containerRef.current.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, [value, getSectionRefs]);

  // Badge summarizing the ticker's latest weight change (Bitcoin Navigator).
  const adjustmentBadge = useMemo(() => {
    if (!navigator) return null;
    const oldW = selectedTickerInfo?.oldW;
    const newW = selectedTickerInfo?.newW;
    if (oldW == null || newW == null) return null;
    const word =
      newW === 0 && oldW > 0
        ? "EXITED"
        : oldW === 0 && newW > 0
          ? "OPENED"
          : newW > oldW
            ? "INCREASED"
            : newW < oldW
              ? "REDUCED"
              : "HELD";
    const color =
      word === "OPENED" || word === "INCREASED"
        ? "#4CAF50"
        : word === "HELD"
          ? "#5B5B5D"
          : "#F44336";
    return {
      text: `${word} · ${(oldW * 100).toFixed(2)}% → ${(newW * 100).toFixed(2)}%`,
      color,
    };
  }, [navigator, selectedTickerInfo?.oldW, selectedTickerInfo?.newW]);

  return (
    <div ref={contentRootRef}>
      <div className="ws-portfolio-back-button-container">
        <div className="ws-portfolio-back-button" onClick={() => setTicker("")}>
          <ArrowBackIcon fontSize="small" />
          {navigator
            ? "Back to Rebalancing History"
            : "Back to view Portfolio Adjustments"}
        </div>
      </div>

      <div className="ws-portfolio-ticker-info__header">
        <div className="ws-portfolio-ticker-general-info">
          <div className="flex align-center" style={{ gap: 8 }}>
            <div
              className={`ws-portfolio-ticker-name ${isBlurred ? "blurred" : ""}`}
            >
              {ticker}
            </div>
            <div className="ws-portfolio-ticker-company">
              {companyDataLoading ? (
                <BeautifulSkeleton width={205} height={20} />
              ) : (
                <span className={`${isBlurred ? "blurred" : ""}`}>
                  {truncateText(tickerDetails.tickerDetails.name, 45)}
                </span>
              )}
            </div>
          </div>
          <div className="ws-portfolio-ticker-price">
            <span className="ws-portfolio-ticker-price__value">
              <span className="ws-portfolio-ticker-current_price">
                {companyDataLoading ? (
                  <BeautifulSkeleton width={60} height={24} />
                ) : (
                  "$" + Number(ohlc.lp)?.toFixed(2)
                )}
              </span>
              <span className="ws-portfolio-ticker-usd">USD</span>
            </span>
            <span className="ws-portfolio-ticker-price__change">
              {companyDataLoading ? (
                <>
                  <BeautifulSkeleton width={55} height={24} />
                  <BeautifulSkeleton width={55} height={24} />
                </>
              ) : (
                <>
                  <span
                    className={`ws-portfolio-ticker-price__change-value price-${ohlc.positivePerformance ? "positive" : "negative"}`}
                  >
                    {ohlc.positivePerformance && "+"}({ohlc.change})
                  </span>
                  <span
                    className={`ws-portfolio-chip ${ohlc.positivePerformance ? "positive" : "negative"}`}
                  >
                    {ohlc.positivePerformance && "+"}
                    {ohlc.performancePercent}%
                  </span>
                </>
              )}
            </span>
          </div>
        </div>

        {!(navigator && tabsHidden) && (
          <Tabs
            value={value}
            onChange={handleChange}
            className="w-full ws-portfolio-ticker-info-tabs"
          >
            {!navigator && <Tab label="Overview" />}
            {showAdjustmentReasoning && <Tab label="Adjustment Reasoning" />}
            <Tab label="Chart" />
            {!navigator && <Tab label="Financials" />}
            {displayDescription && <Tab label="About" />}
          </Tabs>
        )}
      </div>

      <div
        className="ws-portfolio-ticker-info__content blurred-tv"
        ref={overviewRef}
      >
        {!navigator && (
          <div className="ws-portfolio-ticker-performances">
            <div className="ws-portfolio-ticker-performance">
              <div className="ws-portfolio-ticker-performance__title">
                {ohlc.closeType}
              </div>
              <div className="ws-portfolio-ticker-performance__value">
                {companyDataLoading ? (
                  <BeautifulSkeleton width={50} animation="wave" />
                ) : (
                  `$${Number(ohlc.close).toFixed(2)}`
                )}
              </div>
            </div>
            <div className="ws-portfolio-ticker-performance">
              <div className="ws-portfolio-ticker-performance__title">Open</div>
              <div className="ws-portfolio-ticker-performance__value">
                {companyDataLoading ? (
                  <BeautifulSkeleton width={50} animation="wave" />
                ) : (
                  `$${Number(ohlc.open).toFixed(2)}`
                )}
              </div>
            </div>
            <div className="ws-portfolio-ticker-performance">
              <div className="ws-portfolio-ticker-performance__title">
                Performance
              </div>
              {companyDataLoading ? (
                <BeautifulSkeleton width={75} height={23.9} animation="wave" />
              ) : (
                <span
                  className={`ws-portfolio-chip ${ohlc.positivePerformance ? "positive" : "negative"}`}
                >
                  {ohlc.positivePerformance && "+"}
                  {Number(ohlc.performancePercent).toFixed(2)}%
                </span>
              )}
            </div>
            <div className="ws-portfolio-ticker-performance">
              <div className="ws-portfolio-ticker-performance__title">
                Day Range
              </div>
              <div className="ws-portfolio-ticker-performance__value">
                {companyDataLoading ? (
                  <BeautifulSkeleton width={110} animation="wave" />
                ) : (
                  `$${Number(lowToShow).toFixed(2) + " - $" + Number(highToShow).toFixed(2)}`
                )}
              </div>
            </div>
          </div>
        )}

        {!navigator && <div className="ws-portfolio-divider" />}
        {showAdjustmentReasoning && (
          <div
            id="ws-portfolio-ticker-adjustment-reasoning"
            className="w-full"
            ref={adjustmentReasoningRef}
          >
            <div className="ws-portfolio-ticker-adjustment-reasoning">
              <div className="ws-portfolio-ticker-adjustment-reasoning__header">
                <div className="ws-portfolio-ticker-adjustment-reasoning__title">
                  <span>Latest Adjustment Reasoning</span>
                </div>
                <div className="ws-portfolio-ticker-adjustment-reasoning__date">
                  {moment(selectedTickerInfo?.date).format("MMM D, YYYY")}
                </div>
              </div>
              <div className="ws-portfolio-ticker-adjustment-reasoning__content">
                {!!selectedTickerInfo?.reason && (
                  <>
                    {navigator && adjustmentBadge ? (
                      <span
                        className="ws-portfolio-ticker-adjustment-badge"
                        style={{
                          backgroundColor: `${adjustmentBadge.color}14`,
                          color: adjustmentBadge.color,
                        }}
                      >
                        {adjustmentBadge.text}
                      </span>
                    ) : (
                      <div className="ws-portfolio-ticker-adjustment-recommendation">
                        {selectedTickerInfo?.isBuy
                          ? "Reasons to Adjust Up:"
                          : "Reasons to Adjust Down:"}
                      </div>
                    )}
                    <ReactMarkdown>{selectedTickerInfo?.reason}</ReactMarkdown>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="ws-portfolio-divider" />
        <div ref={chartRef} id="ws-portfolio-ticker-chart" className="w-full">
          {ticker && (
            <ExampleChart
              symbol={ticker}
              themeMode={capitalize(themeMode)}
              userSettings={userSettings}
            />
          )}
        </div>

        {!navigator && (
          <>
            <div className="ws-portfolio-divider" />
            <div
              id="ws-portfolio-ticker-financials"
              className={`w-full ${isBlurred ? "blurred" : ""}`}
              ref={financialsRef}
            >
              {memoizedFundamentalData}
            </div>
          </>
        )}

        <div className="ws-portfolio-divider" />

        {displayDescription && (
          <div id="ws-portfolio-ticker-about" className="w-full" ref={aboutRef}>
            <div className="ws-portfolio-ticker-about__title">About</div>
            <div
              className={`ws-portfolio-ticker-about__description ${isBlurred ? "blurred" : ""}`}
            >
              {navigator
                ? displayDescription.split("\n\n").map((paragraph, i) => (
                    <p key={i} className="ws-portfolio-ticker-about__paragraph">
                      {paragraph}
                    </p>
                  ))
                : displayDescription}
            </div>
            {!navigator && (
              <>
                <div className="ws-portfolio-divider" />
                <div className="ws-portfolio-ticker-about__infos">
                  <div className="ws-portfolio-ticker-about__info">
                    <div className="ws-portfolio-ticker-about__info-title">
                      Sector
                    </div>
                    <div className="ws-portfolio-ticker-about__info-value">
                      {tickerDetailsInfo?.sector}
                    </div>
                  </div>
                  <div className="ws-portfolio-ticker-about__info">
                    <div className="ws-portfolio-ticker-about__info-title">
                      Industry
                    </div>
                    <div className="ws-portfolio-ticker-about__info-value">
                      {tickerDetailsInfo?.industry}
                    </div>
                  </div>
                  <div className="ws-portfolio-ticker-about__info">
                    <div className="ws-portfolio-ticker-about__info-title">
                      Employees
                    </div>
                    <div className="ws-portfolio-ticker-about__info-value">
                      {tickerDetailsInfo?.total_employees
                        ? numberWithCommas(tickerDetailsInfo?.total_employees)
                        : "Not Available"}
                    </div>
                  </div>
                  <div className="ws-portfolio-ticker-about__info">
                    <div className="ws-portfolio-ticker-about__info-title">
                      Website
                    </div>
                    <div
                      className={`ws-portfolio-ticker-about__info-value ${isBlurred ? "blurred" : ""}`}
                    >
                      <a
                        href={tickerDetailsInfo?.homepage_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {tickerDetailsInfo?.homepage_url}
                      </a>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const stateToProps = (state) => ({
  themeMode: state.themeMode.themeMode,
  userSettings: state.userSettings.userSettings,
  tickerDetails: state.companyData.companyData,
  companyDataLoading: state.companyData.companyDataLoading,
  marketStatus: state.companyData.companyData.marketState,
  // Same live-price source the shared table renderers use (SimpleDisplay).
  currentPrices: isInternalWS
    ? state.EquitiesSocket.currentPrices
    : state.dxFeedSocket.socketData,
  dxCurrentSubs: state.dxFeedSocket.currentSubs,
});

const dispatchToProps = (dispatch) => ({
  fetchCompanyData: (symbol) => dispatch(companyDataLoad(symbol)),
  setTicker: (sym) => dispatch(portfolioSymbolSetter(sym)),
  listen: (symbols, options, disconnect) =>
    dispatch(dxFeedSocketListen(symbols, options, disconnect)),
});

export default connect(
  stateToProps,
  dispatchToProps,
)(WealthSeriesPortfolioTickerInfo);
